import React, {useEffect, useState} from 'react';
import {Alert, Box, Button, Chip, CircularProgress, Typography} from '@mui/material';
import {toastHelper} from '../../../utils/toast-helper';
import {useGetSensibiliteQuery, useLancerSensibiliteMutation} from '../../../modules/simulations/simulations.api';
import {CARD_SX, Eyebrow} from '../interventions/v2/primitives.js';
import {ANALYSE_SENSIBILITE_ACTIVE, MESSAGE_ANALYSE_DESACTIVEE, etatSensibilite, lignesTornado, messageErreurLecture, pollingSensibilite, referenceTornado} from './sensibiliteRules';

/** Bouton « Analyser la sensibilité » + polling 5 s + tornado (barres HTML/CSS) ; un clic sur une ligne appelle `onAppliquer(ligne)` (brouillon, sans jouer). */
function BlocSensibilite({simulation, onAppliquer, sx}) {
    const id = simulation?.id;
    const [lancee, setLancee] = useState(false);
    const [lancer, {isLoading: envoi}] = useLancerSensibiliteMutation();
    const aUneAnalyse = Boolean(simulation?.sensibiliteStatut) || lancee;
    // La lecture répond 409 tant que rien n'est lancé : on ne l'interroge qu'après un POST ou si la simulation porte déjà un statut.
    const [polling, setPolling] = useState(0);
    const {currentData: courante, isFetching, error: erreurLecture} = useGetSensibiliteQuery(String(id), {skip: !id || !aUneAnalyse, pollingInterval: polling});
    useEffect(() => {
        setPolling(aUneAnalyse ? pollingSensibilite(courante, true, Boolean(erreurLecture)) : 0);
    }, [courante, aUneAnalyse, erreurLecture]);
    const etat = etatSensibilite(courante, {lancee: lancee && !courante && !erreurLecture, erreurLecture: erreurLecture ? messageErreurLecture(erreurLecture) : null});

    const demarrer = async () => {
        if (!ANALYSE_SENSIBILITE_ACTIVE) return; // décision de Jérôme : lancement désactivé (calcul trop long)
        try {
            await lancer(id).unwrap();
            setLancee(true);
        } catch (e) {
            toastHelper.handleError(e, "L'analyse de sensibilité n'a pas pu être lancée.");
        }
    };
    const lignes = etat.etat === 'done' ? lignesTornado(courante) : [];
    const reference = etat.etat === 'done' ? referenceTornado(courante) : null;

    return (
        <Box sx={{...CARD_SX, p: 1.5, ...sx}} data-testid="sensibilite" data-etat={etat.etat}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                <Eyebrow>Sensibilité</Eyebrow>
                {etat.bouton &&
                    <Button size="small" variant="outlined" onClick={demarrer} disabled={!ANALYSE_SENSIBILITE_ACTIVE || envoi || isFetching}
                            sx={{borderRadius: 999, textTransform: 'none', fontWeight: 700}}>{etat.bouton}</Button>}
                {etat.pilule &&
                    <Chip size="small" data-testid="sensibilite-en-cours" icon={<CircularProgress size={12}/>} label={etat.pilule} sx={{fontWeight: 700, bgcolor: '#E7ECF6', color: '#0F2D69'}}/>}
            </Box>
            {etat.bouton && !ANALYSE_SENSIBILITE_ACTIVE &&
                <Typography data-testid="sensibilite-desactivee" sx={{mt: 0.5, fontSize: 12, color: '#8A929C'}}>{MESSAGE_ANALYSE_DESACTIVEE}</Typography>}
            {etat.message && <Alert severity="error" sx={{mt: 1}} data-testid="sensibilite-echec">{etat.message}</Alert>}
            {reference && <Typography data-testid="sensibilite-reference" sx={{mt: 1, fontSize: 13, fontWeight: 700, color: '#0F2D69'}}>{reference}</Typography>}
            {lignes.length > 0 &&
                <Box sx={{mt: 0.5}} role="list" aria-label="sensibilité">
                    {lignes.map((l) => (
                        <Box key={l.cle} role="listitem" data-testid="cran" data-sens={l.sens} onClick={() => onAppliquer?.(l)}
                             title="Appliquer ce cran au volet"
                             sx={{display: 'grid', gridTemplateColumns: 'minmax(140px, 1fr) minmax(0, 2fr)', gap: 1, alignItems: 'center', py: 0.5, px: 0.5,
                                 borderRadius: 2, cursor: onAppliquer ? 'pointer' : 'default', '&:hover': {bgcolor: '#F6F8F4'}}}>
                            <Typography sx={{fontSize: 13, fontWeight: 600}}>{l.libelle}</Typography>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, minWidth: 0}}>
                                {/* Axe central : la barre part du milieu, à gauche si le coût baisse, à droite s'il monte. */}
                                <Box sx={{position: 'relative', flex: '0 0 40%', height: 14}}>
                                    <Box sx={{position: 'absolute', left: '50%', top: 0, bottom: 0, width: '1px', bgcolor: '#C9D0D8'}}/>
                                    <Box data-testid="barre" sx={{position: 'absolute', top: 2, bottom: 2, bgcolor: l.couleur, borderRadius: 999,
                                        width: `${l.largeur / 2}%`, ...(l.sens === 'baisse' ? {right: '50%'} : {left: '50%'})}}/>
                                </Box>
                                <Typography sx={{fontSize: 12, fontWeight: 600, color: '#2D333B'}} noWrap>{l.valeur}</Typography>
                                {l.contrainte && <Chip size="small" label="contrainte" data-testid="pastille-contrainte" sx={{height: 18, fontSize: 11, fontWeight: 700, bgcolor: '#FBE6E6', color: '#B22222'}}/>}
                            </Box>
                        </Box>
                    ))}
                </Box>}
        </Box>
    );
}

export default BlocSensibilite;
